import React from 'react'

interface CanvasMemoIconProps {
  size?: number
  className?: string
}

/**
 * "Notiz platzieren"-Icon der Projekt-Canvas-Toolbar (Figma: `Icon / FileText`,
 * Node `4916:265163` in `btn Canvas Toolbar Note` 4916:265194).
 * Cyan-Paar analog zum gelben `CanvasNoteIcon` — die Farbe kennzeichnet
 * den Notiz-Typ, nicht einen UI-Zustand, deshalb kein Theme-Token.
 *
 * Die Ecke oben rechts ist abgerundet: dort, wo das Blatt umknickt, sitzt eine
 * Kurve statt einer scharfen Ecke. Dieselbe Überarbeitung hat
 * `CanvasNoteIcon` erfahren.
 *
 * **Stand 2026-09-04 (neu aus Figma geholt):** Das Blatt füllt die 24er-Fläche
 * jetzt breiter aus — von `x=3` bis `x=21` statt `4…20` —, der Knick sitzt
 * entsprechend bei `x=15/16`, und die drei Textstriche laufen von `6.8` bis
 * `16.8` (der oberste weiterhin kurz, bis `9.8`).
 *
 * Die Zustände *Default* und *Hover* des Figma-Knopfes tragen dasselbe Glyph
 * (sie unterscheiden sich nur in der Rundung der exportierten Zahlen); der
 * Hover-Zustand ist im Code der Hintergrund der Pille, nicht ein zweites Icon.
 *
 * Eselsohr und die drei Zeilen sind eigene Teile, damit sie sich beim Überfahren einzeln bewegen
 * (#315, `_toolbarIconMotion.scss`) — dieselbe Zeichnung, nur in Stücken. Die Zeilen sind von
 * links nach rechts gezeichnet: Sie wachsen von ihrem Anfang aus.
 */
export const CanvasMemoIcon: React.FC<CanvasMemoIconProps> = ({ size = 24, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={['canvas-memo-icon', className].filter(Boolean).join(' ')}
  >
    <path
      d="M16.586 4.08574C16.2109 3.71067 15.7022 3.49995 15.1718 3.49995L5.0002 3.49995C4.46976 3.49995 3.96105 3.71066 3.58598 4.08574C3.21091 4.46081 3.0002 4.96952 3.0002 5.49995V19.1C3.0002 19.6304 3.21091 20.1391 3.58598 20.5142C3.96105 20.8892 4.46976 21.0999 5.0002 21.0999H19.0002C19.5306 21.0999 20.0393 20.8892 20.4144 20.5142C20.7895 20.1391 21.0002 19.6304 21.0002 19.1V9.32838C21.0002 8.79794 20.7895 8.28924 20.4144 7.91416L16.586 4.08574Z"
      fill="#E5FBFF"
    />
    <g stroke="#0097B3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <g className="canvas-memo-icon__ear">
        <path
          d="M15.0002 3.49995V7.49995C15.0002 8.03038 15.2109 8.53909 15.586 8.91416C15.9611 9.28924 16.4698 9.49995 17.0002 9.49995H21.0002"
          fill="#E5FBFF"
          stroke="none"
        />
        <path d="M15.0002 3.49995V7.49995C15.0002 8.03038 15.2109 8.53909 15.586 8.91416C15.9611 9.28924 16.4698 9.49995 17.0002 9.49995H21.0002" />
      </g>
      <path className="canvas-memo-icon__line canvas-memo-icon__line--1" d="M6.8 9.29995H9.8" />
      <path className="canvas-memo-icon__line canvas-memo-icon__line--2" d="M6.8 13.2999H16.8" />
      <path className="canvas-memo-icon__line canvas-memo-icon__line--3" d="M6.8 17.3H16.8" />
      <path d="M5.0002 3.49995L15.1718 3.49995C15.7022 3.49995 16.2109 3.71067 16.586 4.08574L20.4144 7.91416C20.7895 8.28924 21.0002 8.79794 21.0002 9.32838V19.1C21.0002 19.6304 20.7895 20.1391 20.4144 20.5142C20.0393 20.8892 19.5306 21.0999 19.0002 21.0999H5.0002C4.46976 21.0999 3.96105 20.8892 3.58598 20.5142C3.21091 20.1391 3.0002 19.6304 3.0002 19.1V5.49995C3.0002 4.96952 3.21091 4.46081 3.58598 4.08574C3.96105 3.71066 4.46976 3.49995 5.0002 3.49995Z" />
    </g>
  </svg>
)
